import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowUpRight,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  List,
  Settings2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ItemPlanSheet } from '@/components/manufacturing/planning/ItemPlanSheet';
import {
  CreatePurchaseOrdersDialog,
  type CreatePurchaseOrdersScope,
} from '@/components/manufacturing/planning/CreatePurchaseOrdersDialog';
import { InlineStat, shortDate } from '@/components/manufacturing/planning/shared';
import { addMonths, endOfMonth, monthKey, startOfMonth, type IsoDate } from '@/features/manufacturing/planning/dates';
import {
  buildVendorOrderGroups,
  groupsByDate,
  isDrawnAsGroup,
  summarizeProcurement,
  type VendorOrderGroup,
} from '@/features/manufacturing/planning/procurementCalendar';
import type { CalendarEvent, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { useIsMobile } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';
import { ProcurementAgenda } from './ProcurementAgenda';
import { ProcurementDaySheet } from './ProcurementDaySheet';
import { ProcurementMonthGrid } from './ProcurementMonthGrid';
import {
  ALL_FILTERS,
  DEFAULT_FILTERS,
  FILTER_META,
  MONTH_LONG,
  ORDER_GROUP_FILTER,
  longDate,
  type CalendarFilterKey,
} from './shared';

interface ProcurementCalendarViewProps {
  model: PlanningModel;
  canCreate: boolean;
}

type ViewMode = 'month' | 'list';

function isMonthKey(value: string | null): value is string {
  return !!value && /^\d{4}-\d{2}$/.test(value);
}

/**
 * The procurement calendar proper: the planner's suggestions grouped
 * into one vendor order per day, drawn on a full-width month with a
 * Create PO button on every one of them, plus the deadlines, stock-outs
 * and arrivals around them.
 */
export function ProcurementCalendarView({ model, canCreate }: ProcurementCalendarViewProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const monthParam = params.get('month');
  const monthStart: IsoDate = isMonthKey(monthParam) ? `${monthParam}-01` : startOfMonth(model.today);
  const monthEnd = endOfMonth(monthStart);
  const view: ViewMode = isMobile ? 'list' : params.get('view') === 'list' ? 'list' : 'month';

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        if (value == null) next.delete(key);
        else next.set(key, value);
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const setMonth = (date: IsoDate) =>
    updateParams({ month: monthKey(date) === monthKey(model.today) ? null : monthKey(date) });
  const setView = (next: ViewMode) => updateParams({ view: next === 'month' ? null : next });

  const [filters, setFilters] = useState<Set<CalendarFilterKey>>(() => new Set(DEFAULT_FILTERS));
  const [dayOpen, setDayOpen] = useState<IsoDate | null>(null);
  const [itemOpen, setItemOpen] = useState<string | null>(null);
  const [poDialog, setPoDialog] = useState<{ open: boolean; scope: CreatePurchaseOrdersScope | null }>({
    open: false,
    scope: null,
  });

  const groups = useMemo(() => buildVendorOrderGroups(model), [model]);
  const summary = useMemo(() => summarizeProcurement(model, groups), [model, groups]);
  const allGroupsByDay = useMemo(() => groupsByDate(groups), [groups]);
  const groupsByDay = useMemo(
    () => (filters.has(ORDER_GROUP_FILTER) ? allGroupsByDay : new Map<IsoDate, VendorOrderGroup[]>()),
    [allGroupsByDay, filters],
  );
  const eventsByDay = useMemo(() => {
    const map = new Map<IsoDate, CalendarEvent[]>();
    for (const event of model.events) {
      if (isDrawnAsGroup(event)) continue;
      if (!filters.has(event.kind as CalendarFilterKey)) continue;
      const list = map.get(event.date) ?? [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [model.events, filters]);

  const monthCount = (key: CalendarFilterKey) => {
    if (key === ORDER_GROUP_FILTER) return groups.filter((g) => g.date >= monthStart && g.date <= monthEnd).length;
    return model.events.filter((e) => e.kind === key && !isDrawnAsGroup(e) && e.date >= monthStart && e.date <= monthEnd).length;
  };
  const toggleFilter = (key: CalendarFilterKey) =>
    setFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const openCreate = (scope: CreatePurchaseOrdersScope | null) => setPoDialog({ open: true, scope });
  const createForGroup = (group: VendorOrderGroup) =>
    openCreate({
      orderIds: group.orders.map((o) => o.id),
      heading: `${group.vendorName} · place by ${shortDate(group.latestDate ?? group.date)}`,
    });
  const createForDay = (date: IsoDate, dayGroups: VendorOrderGroup[]) =>
    openCreate({
      orderIds: dayGroups.flatMap((g) => g.orders.map((o) => o.id)),
      heading: `${dayGroups.length} vendors · ${longDate(date)}`,
    });
  const createForToday = () => {
    const draftable = summary.placeNow.filter((g) => !g.unassignable);
    if (draftable.length === 1) createForGroup(draftable[0]);
    else createForDay(model.today, draftable);
  };

  const placeNowDraftable = summary.placeNow.filter((g) => !g.unassignable);
  const isCurrentMonth = monthKey(monthStart) === monthKey(model.today);
  const beyondHorizon = monthStart > model.horizonEnd;
  const monthTitle = MONTH_LONG.format(new Date(`${monthStart}T00:00:00Z`));

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Procurement"
        title="Procurement calendar"
        meta={
          <span className="text-xs text-muted-foreground">
            Driven by production planning · {model.settings.horizonDays}-day horizon · lead times from PO history
          </span>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/mfg/planning')}>
              <CalendarRange className="h-4 w-4" />
              <span className="hidden sm:inline">Production planning</span>
              <span className="sm:hidden">Planning</span>
            </Button>
            <Button
              onClick={() => openCreate(null)}
              disabled={model.orders.length === 0}
              title={model.orders.length === 0 ? 'Nothing to order right now' : undefined}
            >
              <ClipboardList className="h-4 w-4" />
              Create draft POs
              {model.orders.length > 0 && <Badge variant="muted">{model.orders.length}</Badge>}
            </Button>
          </>
        }
      />

      {summary.placeNow.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-fresh-salmon/60 bg-fresh-salmon/10 px-4 py-3 text-sm">
          <div>
            <span className="font-medium text-fresh-salmon-dark">
              {summary.placeNow.length} {summary.placeNow.length === 1 ? 'vendor order needs' : 'vendor orders need'} to go out today
            </span>
            <span className="ml-2 text-muted-foreground">
              {summary.placeNow
                .slice(0, 3)
                .map((g) => g.vendorName)
                .join(' · ')}
              {summary.placeNow.length > 3 ? ' · …' : ''}
              {summary.placeNowCents > 0 && ` · ${formatCurrency(summary.placeNowCents)}`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setDayOpen(model.today)}>
              Review today
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
            {canCreate && placeNowDraftable.length > 0 && (
              <Button size="sm" onClick={createForToday}>
                <ClipboardList className="h-3.5 w-3.5" />
                Create {placeNowDraftable.length === 1 ? 'draft PO' : `${placeNowDraftable.length} draft POs`}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <InlineStat
          label="Place now"
          value={
            summary.placeNow.length === 0
              ? 'Nothing due today'
              : `${summary.placeNow.length} ${summary.placeNow.length === 1 ? 'PO' : 'POs'} · ${formatCurrency(summary.placeNowCents)}`
          }
          tone={summary.placeNow.length > 0 ? 'warn' : 'ok'}
        />
        <InlineStat
          label="Next order day"
          value={
            summary.nextGroup
              ? `${shortDate(summary.nextGroup.date)} · ${summary.nextGroup.vendorName}`
              : 'Nothing further in the horizon'
          }
        />
        <InlineStat
          label="Next deadline"
          value={
            summary.nextDeadline
              ? `${shortDate(summary.nextDeadline.date)} · ${summary.nextDeadline.group.vendorName}`
              : '—'
          }
        />
        <InlineStat
          label="Next stock-out"
          value={
            summary.nextRunout
              ? `${shortDate(summary.nextRunout.date)} · ${model.itemsById.get(summary.nextRunout.itemId ?? '')?.item.name ?? ''}`
              : 'None in the horizon'
          }
          tone={summary.nextRunout ? 'warn' : 'ok'}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" aria-label="Previous month" onClick={() => setMonth(addMonths(monthStart, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label="Next month" onClick={() => setMonth(addMonths(monthStart, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="ml-2 text-lg font-medium">{monthTitle}</h2>
          {!isCurrentMonth && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setMonth(model.today)}>
              Today
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isMobile && (
            <div className="inline-flex rounded-md border border-border bg-card p-0.5" role="group" aria-label="View">
              <Button
                type="button"
                size="sm"
                variant={view === 'month' ? 'secondary' : 'ghost'}
                aria-pressed={view === 'month'}
                onClick={() => setView('month')}
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Month
              </Button>
              <Button
                type="button"
                size="sm"
                variant={view === 'list' ? 'secondary' : 'ghost'}
                aria-pressed={view === 'list'}
                onClick={() => setView('list')}
              >
                <List className="h-3.5 w-3.5" />
                List
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {ALL_FILTERS.map((key) => {
              const meta = FILTER_META[key];
              const on = filters.has(key);
              const count = monthCount(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleFilter(key)}
                  aria-pressed={on}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                    on ? 'border-border bg-card text-foreground' : 'border-transparent bg-secondary text-muted-foreground line-through',
                  )}
                >
                  <span className={cn('h-2 w-2 rounded-full', meta.dot)} />
                  {meta.label}
                  {count > 0 && <span className="tabular-nums text-muted-foreground">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {beyondHorizon && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-secondary/50 px-4 py-2.5 text-xs text-muted-foreground">
          <span>
            {monthTitle} is past the {model.settings.horizonDays}-day planning horizon (ends {shortDate(model.horizonEnd)}), so
            nothing is planned here yet.
          </span>
          <Button size="sm" variant="ghost" onClick={() => navigate('/mfg/planning')}>
            <Settings2 className="h-3.5 w-3.5" />
            Extend it in planning settings
          </Button>
        </div>
      )}

      {view === 'month' ? (
        <div className="flex min-h-[calc(100vh-24rem)] flex-col">
          <ProcurementMonthGrid
            model={model}
            monthStart={monthStart}
            groupsByDay={groupsByDay}
            eventsByDay={eventsByDay}
            canCreate={canCreate}
            onOpenDay={setDayOpen}
            onCreatePo={createForGroup}
          />
        </div>
      ) : (
        <ProcurementAgenda
          model={model}
          from={monthStart}
          to={monthEnd}
          groupsByDay={groupsByDay}
          eventsByDay={eventsByDay}
          canCreate={canCreate}
          onCreatePo={createForGroup}
          onCreateDay={createForDay}
          onOpenItem={setItemOpen}
          emptyText={
            beyondHorizon
              ? `Nothing planned for ${monthTitle} — it is beyond the planning horizon.`
              : `Nothing to order or receive in ${monthTitle}.`
          }
        />
      )}

      <ProcurementDaySheet
        date={dayOpen}
        model={model}
        groups={dayOpen ? (allGroupsByDay.get(dayOpen) ?? []) : []}
        events={dayOpen ? (eventsByDay.get(dayOpen) ?? []) : []}
        canCreate={canCreate}
        onOpenChange={(open) => !open && setDayOpen(null)}
        onCreatePo={createForGroup}
        onCreateDay={createForDay}
        onOpenItem={setItemOpen}
      />

      <ItemPlanSheet
        plan={itemOpen ? (model.itemsById.get(itemOpen) ?? null) : null}
        model={model}
        onOpenChange={(open) => !open && setItemOpen(null)}
      />

      <CreatePurchaseOrdersDialog
        open={poDialog.open}
        onOpenChange={(open) => setPoDialog((s) => ({ ...s, open }))}
        model={model}
        canCreate={canCreate}
        scope={poDialog.scope}
      />
    </div>
  );
}
